import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import globalAxios from "../../globalAxios";
import { SERVER_URL } from "../../config";

export const fetchSchedules = createAsyncThunk(
	"schedules/fetchSchedules",
	async (itemNumbers, { rejectWithValue }) => {
		try {
			const res = await globalAxios.get(`${SERVER_URL}/schedules`, {
				params: { itemNums: itemNumbers },
			});
			return res.data; // array of schedules
		} catch (err) {
			console.error("Failed to fetch schedules", err);
			return rejectWithValue(err.response?.data || err.message);
		}
	}
);

const schedulesSlice = createSlice({
	name: "schedules",
	initialState: {
		data: [], // array of schedule rows
		loading: false,
		error: null,
	},
	reducers: {
		// ⬅️ NEW: merge/upsert schedules returned by PUT /user/events/:id
		upsertSchedules: (state, action) => {
			const incoming = Array.isArray(action.payload)
				? action.payload
				: [];
			if (incoming.length === 0) return;

			const byId = new Map(
				state.data.map((s) => [String(s.item_num), s])
			);
			for (const row of incoming) {
				const key = String(row.item_num);
				// shallow upsert: overwrite or insert
				byId.set(key, { ...(byId.get(key) || {}), ...row });
			}
			state.data = Array.from(byId.values());
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSchedules.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchSchedules.fulfilled, (state, action) => {
				const byId = new Map(
					state.data.map((s) => [String(s.item_num), s])
				);
				for (const row of action.payload || []) {
					byId.set(String(row.item_num), row);
				}
				state.data = Array.from(byId.values());
				state.loading = false;
			})
			.addCase(fetchSchedules.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			});
	},
});

// ✅ Selectors
export const selectSchedulesByItem = (state, itemNum) =>
	state.schedules.data.find((s) => String(s.item_num) === String(itemNum)) ||
	null;

export const selectAllSchedules = (state) => state.schedules.data;

// Prefetch any missing items (skip 51300 if you keep it special-cased)
export const fetchSchedulesIfNeeded =
	(itemNumbers) => async (dispatch, getState) => {
		const state = getState();
		const existing = state.schedules.data;

		const missing = (itemNumbers || []).filter(
			(itemNum) =>
				itemNum !== "51300" &&
				!existing.some((s) => String(s.item_num) === String(itemNum))
		);

		if (missing.length > 0) {
			await dispatch(fetchSchedules(missing));
		}
	};

export const { upsertSchedules } = schedulesSlice.actions;
export default schedulesSlice.reducer;
